/**
 * One dashboard widget, drawn according to its DECLARED KIND (#1188).
 *
 * ── the defect this closes ──────────────────────────────────────────────────
 * `core.widget_kind` is a vocabulary held as data — a kind is a row plus a
 * renderer, deliberately not a CHECK constraint. Measured on 7cd7326f, the one
 * host that renders a dashboard (`apps/portal/src/pages/AtAGlance.tsx`) never
 * reads `item.kind` at all: every widget, chart or list or timeline, was drawn
 * as the same KPI paragraph. The grammar was decorative at the exact place it
 * is supposed to decide something.
 *
 * ── what this does NOT decide ───────────────────────────────────────────────
 * Authority. Whether a deployment registers a kind, and whether that kind is
 * enabled, is settled at the door — `2026090604:508` refuses a disabled kind on
 * write and `2026090606:134` refuses one on describe, each in its own sentence.
 * This component never re-litigates that; it answers the different question of
 * whether THIS BUILD ships a renderer, and says so plainly when it does not.
 *
 * It also fetches nothing. Like `InsightCard`, which it delegates to, it
 * receives already-governed, already-resolved content: rows the host was
 * allowed to read, a chart definition the host already validated, a drill key
 * the host resolves through its own navigation. A widget cannot manufacture
 * authority, a route, or a row.
 */
import type { ReactNode } from 'react'
import { rendererFor, type DescribedItem } from '@solum/experience'
import type { ChartDefinition } from '@solum/experience'
import { InsightCard, type InsightCardProps } from './InsightCard'
import { chartAdapterProblem, InsightChart, type InsightDatum } from '../charts/InsightChart'
import type { ExperienceState } from './StateBoundary'
import { cellText, DataTable, type DataTableColumn } from '../DataTable'
import s from './DashboardWidget.module.css'

/** The shared table's column, under the name this widget's callers already
 *  use. One declaration, so a column that renders here renders the same way in
 *  every other data table. */
export type DashboardWidgetColumn = DataTableColumn

export interface DashboardWidgetProps {
  /** The governed description. Its `kind` is what decides the renderer. */
  item: Pick<DescribedItem, 'itemKey' | 'kind' | 'title' | 'description' | 'drill' | 'drillDescriptor'>
  state?: ExperienceState
  /** `kpi`: the already-computed figure. */
  value?: ReactNode
  comparison?: ReactNode
  /**
   * `list`: the columns the view declared and the rows the host was allowed to
   * read. `onOpen` receives the row so the HOST can resolve the item's governed
   * drill key through its own navigation — this component never builds a route.
   */
  list?: {
    columns: readonly DashboardWidgetColumn[]
    rows: readonly Record<string, unknown>[]
    onOpen?: (row: Record<string, unknown>) => void
    canOpen?: (row: Record<string, unknown>) => boolean
  }
  /** `chart`: a definition the host already validated, and governed rows. */
  chart?: {
    definition: ChartDefinition
    rows: readonly InsightDatum[]
    onOpen?: (row: InsightDatum) => void
    canOpen?: (row: InsightDatum) => boolean
  }
  action?: InsightCardProps['action']
  freshness?: InsightCardProps['freshness']
  testId?: string
}


export function DashboardWidget({
  item, state, value, comparison, list, chart, action, freshness, testId,
}: DashboardWidgetProps) {
  const choice = rendererFor(item.kind)
  const visualLabel = representationLabel(item.kind, choice.renderer)
  const title = item.title ?? item.itemKey
  const intent = item.description ?? 'A governed item from your own data.'
  /* The card's header metric slot is NOT used. A kpi's figure IS its "Value"
     representation — the body — and passing it to the header as well drew the
     same number twice, 27px in the header and again in the body directly under
     it (design-sync kit finding, DashboardWidget.tsx:~168). One figure, in the
     representation the reader chose; the Data table carries it in the other. */
  const card = (parts: Pick<InsightCardProps, 'visual' | 'table' | 'narrative'>) => (
    <InsightCard
      title={title}
      intent={intent}
      state={state}
      freshness={freshness}
      action={action}
      visualLabel={visualLabel}
      testId={testId ?? `dashboard-widget-${item.itemKey}`}
      {...parts}
    />
  )

  if (choice.renderer === null) {
    /* NAMED, not blank. A widget that draws nothing and says nothing is
       indistinguishable from one whose data is empty, and the person looking at
       it has no way to tell which. The deployment is not wrong to hold a kind
       this build cannot draw — so this is an explanation, not an error state. */
    return card({
      visual: <p data-testid="dashboard-widget-unrenderable">{choice.reason}</p>,
      table: <p>{choice.reason}</p>,
      narrative: <p>{choice.reason}</p>,
    })
  }

  if (choice.renderer === 'chart') {
    if (!chart) {
      const absent = 'This chart has no definition to draw.'
      return card({
        visual: <p data-testid="dashboard-widget-missing-content">{absent}</p>,
        table: <p>{absent}</p>,
        narrative: <p>{absent}</p>,
      })
    }
    const adapterProblem = chartAdapterProblem(chart.definition)
    if (adapterProblem) {
      return card({
        visual: <p role="alert" data-testid="dashboard-widget-chart-refused">{adapterProblem}</p>,
        table: <p>{adapterProblem}</p>,
        narrative: <p>{adapterProblem}</p>,
      })
    }
    if (chart.rows.length === 0) {
      return card({
        visual: <p data-testid="dashboard-widget-chart-empty">{chart.definition.emptyState}</p>,
        table: <p>{chart.definition.emptyState}</p>,
        narrative: <p>{chart.definition.emptyState}</p>,
      })
    }
    // The adapter from #1749, not a second one. It owns which chart kinds it
    // can draw and refuses the rest by name.
    return card({
      visual: <InsightChart definition={chart.definition} rows={chart.rows}
        /* The card's own intent, not a description of the chart type. A
           screen reader hearing "a line chart" learns nothing it could act on. */
        accessibleDescription={typeof intent === 'string' ? intent : title}
        onActivate={chart.onOpen}
        testId={`dashboard-widget-chart-${item.itemKey}`} />,
      table: <DataTable caption={title} rows={chart.rows}
        columns={chartColumns(chart.definition)} onOpen={chart.onOpen} canOpen={chart.canOpen} />,
      narrative: <p>{intent}</p>,
    })
  }

  if (choice.renderer === 'list') {
    if (!list) {
      const absent = 'This list has no rows to show.'
      return card({
        visual: <p data-testid="dashboard-widget-missing-content">{absent}</p>,
        table: <p>{absent}</p>,
        narrative: <p>{absent}</p>,
      })
    }
    const table = <DataTable caption={title} columns={list.columns} rows={list.rows}
      onOpen={list.onOpen} canOpen={list.canOpen} />
    return card({
      visual: (
        <ul className={s.list} data-testid={`dashboard-widget-list-${item.itemKey}`}>
          {list.rows.map((row, index) => {
            const label = cellText(row[list.columns[0]?.key ?? ''])
            return (
              <li key={index}>
                {list.onOpen && (list.canOpen?.(row) ?? true)
                  /* The row opens through the HOST. The governed drill key is
                     the item's, and resolving it is navigation the host owns —
                     this component has never seen a route. */
                  ? <button type="button" onClick={() => list.onOpen?.(row)}>{label}</button>
                  : <span>{label}</span>}
                {list.columns.slice(1).map((column) => (
                  <span key={column.key} className={s.fact}>{cellText(row[column.key])}</span>
                ))}
              </li>
            )
          })}
        </ul>
      ),
      table,
      narrative: <p>{intent}</p>,
    })
  }

  // kpi — the figure, once, with its comparison beside it.
  return card({
    visual: (
      <div className={s.kpi}>
        <strong className={`${s.figure} tnum`} data-testid={`dashboard-widget-value-${item.itemKey}`}>
          {value ?? '—'}
        </strong>
        {comparison != null ? <span className="supporting">{comparison}</span> : null}
      </div>
    ),
    table: <DataTable caption={title} columns={[{ key: 'value', label: title }]}
      rows={[{ value: value ?? null }]} />,
    narrative: <p>{intent}</p>,
  })
}

function representationLabel(kind: string, renderer: 'kpi' | 'list' | 'chart' | null) {
  if (renderer === 'chart') return 'Chart'
  if (renderer === 'list') return 'List'
  if (renderer === 'kpi') return 'Value'
  const words = kind.trim().replaceAll(/[-_]+/gu, ' ')
  return words ? `${words[0]!.toUpperCase()}${words.slice(1).toLowerCase()}` : 'Item'
}

/** The columns a chart's own encoding names, so its table says the same thing. */
function chartColumns(definition: ChartDefinition): DashboardWidgetColumn[] {
  const encoding = definition.encoding
  const fields = [encoding.timeField, encoding.dimension, encoding.series, encoding.measure,
    encoding.comparisonMeasure, encoding.baselineMeasure]
  return fields.filter((field): field is string => typeof field === 'string' && field.length > 0)
    .map((field) => ({ key: field, label: field.replaceAll('-', ' ').replaceAll('_', ' ') }))
}
